import { ENTITY_PARAM_ORDER, type CustomParam, type EntityParamKey } from './buildTestLink';
import type { PickerList } from './linkBuilderOptions';

/**
 * Per-browser conveniences only: the last form and the last few links. Storage can be missing or
 * throw (private windows, blocked site data), so every access is guarded and the page works
 * without it.
 */
const STATE_KEY = 'zuul:test-link-builder:state';
const RECENT_KEY = 'zuul:test-link-builder:recent';
const RECENT_LIMIT = 10;

export interface BuilderState {
  siteId: number | null;
  domainId: number | null;
  showAllDomains: boolean;
  /** Off: with a landing page group picked, landing pages list only the group's. */
  showAllLandingPages: boolean;
  path: string;
  entities: Partial<Record<EntityParamKey, number | null>>;
  custom: CustomParam[];
}

export const DEFAULT_STATE: BuilderState = {
  siteId: null,
  domainId: null,
  showAllDomains: false,
  showAllLandingPages: false,
  path: '/api/user',
  entities: {},
  custom: [],
};

const read = (key: string): unknown => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
};

const write = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Not persisting is fine.
  }
};

const idOrNull = (value: unknown): number | null => (Number.isInteger(value) ? (value as number) : null);

const isCustomParam = (p: unknown): p is CustomParam =>
  !!p &&
  typeof p === 'object' &&
  typeof (p as CustomParam).key === 'string' &&
  typeof (p as CustomParam).value === 'string';

export function loadState(): BuilderState {
  const saved = read(STATE_KEY);
  if (!saved || typeof saved !== 'object') return DEFAULT_STATE;
  const s = saved as Record<string, unknown>;

  const entities: BuilderState['entities'] = {};
  if (s.entities && typeof s.entities === 'object') {
    const raw = s.entities as Record<string, unknown>;
    ENTITY_PARAM_ORDER.forEach((key) => {
      if (key in raw) entities[key] = idOrNull(raw[key]);
    });
  }

  return {
    siteId: idOrNull(s.siteId),
    domainId: idOrNull(s.domainId),
    showAllDomains: s.showAllDomains === true,
    showAllLandingPages: s.showAllLandingPages === true,
    path: typeof s.path === 'string' ? s.path : DEFAULT_STATE.path,
    entities,
    custom: Array.isArray(s.custom) ? s.custom.filter(isCustomParam) : [],
  };
}

export const saveState = (state: BuilderState): void => write(STATE_KEY, state);

export function loadRecent(): string[] {
  const saved = read(RECENT_KEY);
  return Array.isArray(saved) ? saved.filter((u): u is string => typeof u === 'string').slice(0, RECENT_LIMIT) : [];
}

export function rememberLink(url: string, recent: string[]): string[] {
  const next = [url, ...recent.filter((u) => u !== url)].slice(0, RECENT_LIMIT);
  write(RECENT_KEY, next);
  return next;
}

/** A list only gets a say once it has loaded; while loading or failed, saved ids are kept. */
const known = (list: PickerList, id: number | null | undefined): boolean =>
  id == null || list.loading || list.error || list.options.some((option) => option.id === id);

/**
 * Clears saved ids that no longer exist (deleted entities), keeping the rows themselves. Returns
 * the same object when nothing changed, so it is safe to run in an effect on every list change.
 */
export function pruneStale(
  state: BuilderState,
  lists: { sites: PickerList; domains: PickerList; entities: Record<EntityParamKey, PickerList> }
): BuilderState {
  const staleEntities = ENTITY_PARAM_ORDER.filter(
    (key) => key in state.entities && !known(lists.entities[key], state.entities[key])
  );
  const siteStale = !known(lists.sites, state.siteId);
  const domainStale = !known(lists.domains, state.domainId);
  if (!siteStale && !domainStale && staleEntities.length === 0) return state;

  const entities = { ...state.entities };
  staleEntities.forEach((key) => {
    entities[key] = null;
  });
  return {
    ...state,
    siteId: siteStale ? null : state.siteId,
    domainId: domainStale ? null : state.domainId,
    entities,
  };
}
